import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Popup } from "semantic-ui-react";

import { SnoozeModal, useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES, SNOOZE_STATUSES, SNOOZE_TYPES } from "models";
import { useAppendSnoozeMutation, useDeleteSnoozeMutation } from "modules/CarDetails/components/Snooze/hooks";
import "modules/CarDetails/components/Snooze/Snooze.scss";
import { useCarDealer, useCarLocation } from "modules/CarDetails/hooks";

import type { DeleteSnoozePayload } from "components";
import type { Snooze as SnoozeModel } from "models";
import type { ItemWithHistory } from "utils/itemsWithHistory";

type SnoozeProps = {
  snoozeItemsWithHistory: ItemWithHistory<SnoozeModel>;
  showOwnerInfo?: boolean;
};

export const Snooze = ({ snoozeItemsWithHistory, showOwnerInfo }: SnoozeProps) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const carDealer = useCarDealer();
  const carLocation = useCarLocation();

  const canCreateSnooze = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.Snooze);
  const { mutateAsync: appendSnooze, isPending: isAppendSnoozePending } = useAppendSnoozeMutation();
  const { mutateAsync: deleteSnooze, isPending: isDeleteSnoozePending } = useDeleteSnoozeMutation();

  if (snoozeItemsWithHistory.snooze_status_id === SNOOZE_STATUSES.Deleted) return null;

  const handleAppendSnooze = async (snooze: SnoozeModel) => {
    if (!canCreateSnooze) return;
    await appendSnooze(snooze);
  };

  const handleDeleteSnooze = async (snooze: DeleteSnoozePayload) => {
    await deleteSnooze(snooze);
  };

  return (
    <>
      <div className="Snooze">
        {showOwnerInfo && snoozeItemsWithHistory.user && (
          <Popup
            hoverable
            content={
              <div className="owner-info">
                <p className="owner-title">{t("v8_owner", "Owner")}</p>
                <p className="owner-email">
                  {`${t("v8_private_email", "Private email")}:`} {snoozeItemsWithHistory.user.email}
                </p>
              </div>
            }
            header={`${snoozeItemsWithHistory.user.first_name} ${snoozeItemsWithHistory.user.last_name}`}
            trigger={
              <Button className="tiLightPurple af" color="orange" disabled={!Object.values(SNOOZE_TYPES).includes(snoozeItemsWithHistory.snooze_type_id)}>
                <Icon className="user" />
              </Button>
            }
          />
        )}
        <Button className="wdSkyblue af" color="orange" onClick={() => setIsOpen(true)} disabled={!canCreateSnooze || !snoozeItemsWithHistory.snooze_type_id}>
          <Icon className="clock" />
        </Button>
      </div>

      <SnoozeModal
        dealer={carDealer}
        location={carLocation}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        data={snoozeItemsWithHistory}
        onAppendSnooze={handleAppendSnooze}
        onDeleteSnooze={handleDeleteSnooze}
        isAppendSnoozeLoading={isAppendSnoozePending}
        isDeleteSnoozeLoading={isDeleteSnoozePending}
      />
    </>
  );
};
